import i18n from '../i18n';

let popupHost = null;
let nativeAlert = null;

function t(key) {
  return i18n.t(key);
}

function cleanupMessage(message = "") {
  return String(message || "")
    .replace(/[✅❌⚠️]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function inferVariant(text = "", fallback = "info") {
  const value = cleanupMessage(text).toLowerCase();
  if (!value) return fallback;
  if (
    /(success|saved|created|updated|resent|sent|processed|credited|applied|executed|completed successfully)/i.test(
      value,
    )
  )
    return "success";
  if (
    /(warning|archive|delete|remove|reset|re-import|cannot be undone|are you sure|completed\?|reverse|deactivate)/i.test(
      value,
    )
  )
    return "warning";
  if (
    /(fail|failed|error|invalid|expired|required|missing|unable|not found|limit reached|please fix|must be|select|choose|enter valid)/i.test(
      value,
    )
  )
    return "error";
  return fallback;
}

function inferTitle(kind, message, explicitTitle, variant) {
  if (explicitTitle) return explicitTitle;

  const normalized = String(message || "").trim();
  if (normalized.includes("\n\n")) {
    return normalized.split(/\n\n/)[0].trim();
  }

  if (kind === "confirm") {
    if (/(delete|remove|reset|re-import|cannot be undone)/i.test(normalized))
      return t("popup.titles.confirmDestructive");
    if (
      /(archive|activate|execute|send|resend|mark|reverse|trigger)/i.test(
        normalized,
      )
    )
      return t("popup.titles.pleaseConfirm");
    return t("popup.titles.confirmAction");
  }

  if (variant === "success") return t("popup.titles.success");
  if (variant === "error") return t("popup.titles.attention");
  if (variant === "warning") return t("popup.titles.pleaseReview");
  return t("popup.titles.information");
}

function inferConfirmLabel(message, explicitLabel) {
  if (explicitLabel) return explicitLabel;
  const normalized = String(message || "").toLowerCase();
  if (normalized.includes("delete")) return t("popup.confirmLabels.delete");
  if (normalized.includes("archive")) return t("popup.confirmLabels.archive");
  if (normalized.includes("activate")) return t("popup.confirmLabels.activate");
  if (normalized.includes("execute")) return t("popup.confirmLabels.execute");
  if (normalized.includes("resend") || normalized.includes("send"))
    return t("popup.confirmLabels.send");
  if (normalized.includes("trigger")) return t("popup.confirmLabels.trigger");
  if (normalized.includes("remove")) return t("popup.confirmLabels.remove");
  if (normalized.includes("mark")) return t("popup.confirmLabels.continue");
  return t("popup.labels.confirm");
}

function normalizeMessage(input) {
  if (typeof input === "string") {
    const parts = input.split(/\n\n/);
    if (parts.length > 1) {
      return {
        title: parts[0].trim(),
        message: parts.slice(1).join("\n\n").trim(),
      };
    }
    return { message: input };
  }

  return {
    title: input?.title,
    message: input?.message || "",
    details: input?.details,
    confirmLabel: input?.confirmLabel,
    cancelLabel: input?.cancelLabel,
    variant: input?.variant,
  };
}

function ensureHost() {
  return popupHost;
}

function fallbackAlert(message) {
  if (nativeAlert) {
    nativeAlert(message);
  }
}

export function registerPopupHost(host) {
  popupHost = host;
}

export function unregisterPopupHost() {
  popupHost = null;
}

export function showAlert(input, overrides = {}) {
  const base = normalizeMessage(input);
  const merged = { ...base, ...overrides };
  const variant =
    merged.variant ||
    inferVariant(`${merged.title || ""} ${merged.message || ""}`, "info");
  const payload = {
    kind: "alert",
    variant,
    title: inferTitle("alert", merged.message, merged.title, variant),
    message: merged.message || "",
    details: merged.details,
    confirmLabel: merged.confirmLabel || t("popup.labels.alert"),
  };

  const host = ensureHost();
  if (!host) {
    fallbackAlert(
      [payload.title, payload.message].filter(Boolean).join("\n\n"),
    );
    return Promise.resolve();
  }

  return host.open(payload);
}

export function showConfirm(input, overrides = {}) {
  const base = normalizeMessage(input);
  const merged = { ...base, ...overrides };
  const variant =
    merged.variant ||
    inferVariant(`${merged.title || ""} ${merged.message || ""}`, "warning");
  const payload = {
    kind: "confirm",
    variant,
    title: inferTitle("confirm", merged.message, merged.title, variant),
    message: merged.message || "",
    details: merged.details,
    confirmLabel: inferConfirmLabel(merged.message, merged.confirmLabel),
    cancelLabel: merged.cancelLabel || t("popup.labels.cancel"),
  };

  const host = ensureHost();
  if (!host) {
    return Promise.resolve(window.confirm(payload.message || payload.title));
  }

  return host.open(payload);
}

/** Route all alert() calls through the themed popup. Use showConfirm() for confirmations. */
export function installPopupOverrides() {
  if (typeof window === "undefined") return;
  if (!nativeAlert) nativeAlert = window.alert.bind(window);

  window.alert = (input) => {
    showAlert(typeof input === "string" ? input : String(input ?? ""));
  };
}
